import { useState, useEffect, useMemo, useCallback } from 'react';
import { smsApi, ApiResponse } from '../services/api';
import { useGlobalToast } from '../contexts/ToastContext';
import { transformBatchedHistoryToChartData } from '../utils/chartUtils';

interface ChartDataPoint {
  time: string;
  volume: number;
  cost: number;
  successRate: number;
}

interface SMSDeliveryBreakdown {
  categories: Array<{
    category: {
      id: string;
      name: string;
      description: string;
      color: string;
    };
    count: number;
    percentage: number;
  }>;
  total: number;
}

interface SMSStats {
  totalSent: number;
  totalCost: number;
  deliveryRate: number;
  deliveryStatusBreakdown?: SMSDeliveryBreakdown;
}

interface SMSAnalyticsData extends SMSStats {
  isLoading: boolean;
  hasData: boolean;
}

interface UseSMSAnalyticsReturn extends SMSAnalyticsData {
  // Chart data
  volumeChartData: ChartDataPoint[];
  costChartData: ChartDataPoint[];
  successRateChartData: ChartDataPoint[];

  // Actions
  refreshAnalytics: () => Promise<void>;
  loadAnalytics: () => Promise<void>;
}

interface UseSMSAnalyticsProps {
  selectedTownId?: string;
  dateRange?: { start: string; end: string };
}

interface StatisticsData {
  thisMonth?: number;
  totalCost?: number;
  deliveryRate?: number;
  deliveryStatusBreakdown?: SMSDeliveryBreakdown;
}

interface HistoryData {
  batches?: unknown[];
}

export const useSMSAnalytics = ({
  selectedTownId,
  dateRange
}: UseSMSAnalyticsProps = {}): UseSMSAnalyticsReturn => {
  const { error } = useGlobalToast();

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState<SMSStats>({
    totalSent: 0,
    totalCost: 0,
    deliveryRate: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

  // Load analytics data
  const loadAnalytics = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    console.log('[useSMSAnalytics] Loading analytics data...', { selectedTownId, dateRange });

    try {
      // Load analytics data concurrently
      const [statsRes, historyRes] = await Promise.allSettled([
        smsApi.getStatistics(),
        smsApi.getBatchedHistory({
          townId: selectedTownId,
          dateStart: dateRange?.start,
          dateEnd: dateRange?.end,
        })
      ]);

      // Handle statistics
      if (statsRes.status === 'fulfilled') {
        const res = statsRes.value as ApiResponse<Record<string, unknown>>;
        if (res.success && res.data) {
          const stats = res.data as StatisticsData; // Use specific type instead of any
          console.log('[useSMSAnalytics] Statistics loaded:', stats);

          setAnalyticsData({
            totalSent: stats.thisMonth || 0,
            totalCost: stats.totalCost || 0,
            deliveryRate: stats.deliveryRate || 0,
            deliveryStatusBreakdown: stats.deliveryStatusBreakdown
          });
        } else {
          console.log('[useSMSAnalytics] No statistics data available');
          setAnalyticsData({
            totalSent: 0,
            totalCost: 0,
            deliveryRate: 0
          });
        }
      } else {
        console.error('[useSMSAnalytics] Statistics loading error:', statsRes.reason);
        setAnalyticsData({
          totalSent: 0,
          totalCost: 0,
          deliveryRate: 0
        });
      }

      // Handle batched history for charts
      if (historyRes.status === 'fulfilled') {
        const res = historyRes.value as ApiResponse<Record<string, unknown>>;
        if (res.success && res.data) {
          console.log('[useSMSAnalytics] History data loaded');
          // Process batched history into chart data
          const historyData = res.data as HistoryData; // Use specific type instead of any
          const processedChartData = transformBatchedHistoryToChartData(historyData.batches || []);
          setChartData(processedChartData);
        } else {
          console.log('[useSMSAnalytics] No history data available');
          setChartData([]);
        }
      } else {
        console.error('[useSMSAnalytics] History loading error:', historyRes.reason);
        setChartData([]);
      }
    } catch (err) {
      console.error('[useSMSAnalytics] Error loading analytics:', err);
      error('Failed to load analytics data');
      setAnalyticsData({
        totalSent: 0,
        totalCost: 0,
        deliveryRate: 0
      });
      setChartData([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedTownId, dateRange, error]);

  const refreshAnalytics = async (): Promise<void> => {
    await loadAnalytics();
  };

  // Computed chart data
  const volumeChartData = useMemo(() => {
    return chartData.map(point => ({
      time: point.time,
      volume: point.volume,
      cost: point.cost,
      successRate: point.successRate
    }));
  }, [chartData]);

  const costChartData = useMemo(() => {
    return chartData.map(point => ({
      time: point.time,
      volume: point.volume,
      cost: point.cost,
      successRate: point.successRate
    }));
  }, [chartData]);

  const successRateChartData = useMemo(() => {
    return chartData.map(point => ({
      time: point.time,
      volume: point.volume,
      cost: point.cost,
      successRate: point.successRate
    }));
  }, [chartData]);

  const hasData = analyticsData.totalSent > 0 || chartData.length > 0;

  // Effects
  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  return {
    // State
    ...analyticsData,
    isLoading,
    hasData,

    // Chart data
    volumeChartData,
    costChartData,
    successRateChartData,

    // Actions
    refreshAnalytics,
    loadAnalytics,
  };
};
